import { ProgressProps } from './props';
import styles from './styles.module.scss';

/**
 * Progress Component
 * A customizable progress bar component that can show loading state and different visual styles.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.progress - The progress value as a percentage (0-100)
 * @param {string} [props.color] - Custom color for the progress bar
 * @param {boolean} [props.isLoading] - Whether to show loading skeleton state
 * @param {string} [props.size] - Size variant of the progress bar
 * @param {boolean} [props.useCardColor] - Whether to use card background color
 * @param {boolean} [props.useContainerColor] - Whether to use container background color
 * @returns {JSX.Element} The rendered progress bar component
 *
 * @example
 * // Basic usage
 * <Progress progress={75} />
 *
 * // With custom styling
 * <Progress
 *   progress={50}
 *   color="#ff0000"
 *   size="large"
 *   useCardColor={true}
 *   useContainerColor={false}
 *   isLoading={false}
 * />
 */
export function Progress({
  progress,
  color,
  isLoading,
  size,
  useCardColor,
  useContainerColor,
}: ProgressProps) {
  if (isLoading) {
    return (
      <div
        className={`${styles.container} ${useContainerColor ? styles['container--useContainerColor'] : ''} ${styles[`container--${size}`]}`}
      >
        <div className={styles.container__progress__skeleton} />
      </div>
    );
  }
  return (
    <div
      className={`${styles.container} ${useContainerColor ? styles['container--useContainerColor'] : ''} ${useCardColor ? styles['container--card-color'] : ''} ${styles[`container--${size}`]}`}
    >
      <div
        className={`${styles.container__progress} ${color ? styles['container__progress--customColor'] : ''}`}
        style={
          color
            ? //@ts-ignore
              { '--customColor': color, width: `${progress}%` }
            : { width: `${progress}%` }
        }
      />
    </div>
  );
}
